import type {Store} from 'redux';
import {selectIsMealOptionsEventType} from '~/common/fabric/reducers/widgets/selectors';
import {EventFeature} from '~/common/types/event';

export const FEATURE_QA_IDS: Record<EventFeature, string | ((store: Store) => string)> = {
  // Add to this dictionary as new logic necessitates universal QA ID declarations for invitation features
  [EventFeature.WISHLIST]: 'event-gifting__option-registry',
  [EventFeature.CHARITY]: 'event-gifting__option-donation',
  [EventFeature.CROWDFUNDING]: 'event-gifting__option-crowdfunding',
  [EventFeature.POLLS]: (store) =>
    selectIsMealOptionsEventType(store.getState()) ? 'button_add-meal' : 'button_add-poll',
  [EventFeature.WHAT_TO_BRING]: 'button_what-to-bring',
} as Record<EventFeature, string | ((store: Store) => string)>;

export enum FeatureControllerIdentifier {
  EDIT_TITLE_MODULE = 'edit-title-module',
  EDIT_EVENT_TITLE = 'edit-event-title',
  EDIT_EVENT_DATE = 'edit-event-date',
  EDIT_HOST_DETAILS_MODULE = 'edit-host-details-module',
  EDIT_HOSTED_BY = 'edit-hosted-by',
  EDIT_GUESTS_OF_HONOR = 'edit-guests-of-honor',
  EDIT_LOCATION = 'edit-location',
  EDIT_HOST_NOTE = 'edit-host-note',
  EDIT_ADDITIONAL_LINKS = 'edit-additional-links',
  EDIT_GUEST_OPTIONS = 'edit-guest-options',
  EDIT_HOST_PHOTOS = 'edit-host-photos',
  REMOVE_HOST_PHOTOS = 'remove-host-photos',
  REMOVE_SHARED_PHOTOS = 'remove-shared-photos',
  EDIT_GIFTING_MODULE = 'edit-gifting-module',
  EDIT_REGISTRIES = 'edit-registries',
  REMOVE_GIFTING = 'remove-gifting',
  EDIT_DONATIONS_MODULE = 'edit-donations-module',
  EDIT_CHARITY = 'edit-charity',
  EDIT_CROWDFUND = 'edit-crowdfund',
  EDIT_POLL = 'edit-poll',
  EDIT_WTB = 'edit-wtb',
}

export const FEATURE_CONTROLLER_IDENTIFIER_CONFIG: Record<
  FeatureControllerIdentifier,
  {
    type: 'input' | 'textarea' | 'button' | 'action';
    desktopIdentifier?: string | ((store: Store) => string);
  }
> = {
  [FeatureControllerIdentifier.EDIT_TITLE_MODULE]: {
    type: 'button',
  },
  [FeatureControllerIdentifier.EDIT_EVENT_TITLE]: {
    type: 'input',
    desktopIdentifier: 'event-title',
  },
  [FeatureControllerIdentifier.EDIT_EVENT_DATE]: {
    type: 'button',
    desktopIdentifier: 'event-date-trigger',
  },
  [FeatureControllerIdentifier.EDIT_LOCATION]: {
    type: 'button',
    desktopIdentifier: 'event-location-trigger',
  },
  [FeatureControllerIdentifier.EDIT_HOST_DETAILS_MODULE]: {
    type: 'button',
  },
  [FeatureControllerIdentifier.EDIT_HOSTED_BY]: {
    type: 'button',
    desktopIdentifier: 'event-host-details',
  },
  [FeatureControllerIdentifier.EDIT_GUESTS_OF_HONOR]: {
    type: 'button',
    desktopIdentifier: 'guests-of-honor-toggle',
  },
  [FeatureControllerIdentifier.EDIT_ADDITIONAL_LINKS]: {
    type: 'button',
    desktopIdentifier: 'button_event-links',
  },
  [FeatureControllerIdentifier.EDIT_HOST_NOTE]: {
    type: 'textarea',
    desktopIdentifier: 'event-notes',
  },
  [FeatureControllerIdentifier.EDIT_GUEST_OPTIONS]: {
    type: 'button',
  },
  [FeatureControllerIdentifier.EDIT_HOST_PHOTOS]: {
    type: 'button',
    desktopIdentifier: 'host-photo-gallery-toggle',
  },
  [FeatureControllerIdentifier.REMOVE_HOST_PHOTOS]: {
    type: 'action',
  },
  [FeatureControllerIdentifier.REMOVE_SHARED_PHOTOS]: {
    type: 'action',
  },
  [FeatureControllerIdentifier.EDIT_GIFTING_MODULE]: {
    type: 'button',
  },
  [FeatureControllerIdentifier.EDIT_REGISTRIES]: {
    type: 'button',
    desktopIdentifier: FEATURE_QA_IDS[EventFeature.WISHLIST],
  },
  [FeatureControllerIdentifier.REMOVE_GIFTING]: {
    type: 'action',
  },
  [FeatureControllerIdentifier.EDIT_DONATIONS_MODULE]: {
    type: 'button',
  },
  [FeatureControllerIdentifier.EDIT_CHARITY]: {
    type: 'button',
    desktopIdentifier: FEATURE_QA_IDS[EventFeature.CHARITY],
  },
  [FeatureControllerIdentifier.EDIT_CROWDFUND]: {
    type: 'button',
    desktopIdentifier: FEATURE_QA_IDS[EventFeature.CROWDFUNDING],
  },
  [FeatureControllerIdentifier.EDIT_POLL]: {
    type: 'button',
    desktopIdentifier: FEATURE_QA_IDS[EventFeature.POLLS],
  },
  [FeatureControllerIdentifier.EDIT_WTB]: {
    type: 'button',
    desktopIdentifier: FEATURE_QA_IDS[EventFeature.WHAT_TO_BRING],
  },
};
